import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  SplitSquareHorizontal,
} from "lucide-react";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { MFReviewAccount } from "state/members/accessReviews";
import CustomCheckbox from "./Checkbox";
import AccountRow, { GroupActionButtons } from "./AccountRow";
import { Avatar } from "components/ds";
import FloatingScrollbar from "./FloatingScrollbar";
import {
  applyColumnResize,
  CHECKBOX_TRACK_PX,
  FROZEN_LEFT_OFFSETS,
  FROZEN_REVIEW_COLUMN,
  isAccountDecided,
  MIN_COLUMN_WIDTH,
  orderPinnedColumns,
  type NameSortDirection,
  rowActionButtonCount,
  previousReviewColumnLabel,
  reviewTableMaxHeight,
} from "../reviewLogic";
import { useFrozenPanes } from "./useFrozenPanes";

/**
 * ColumnResizeHandle — a thin drag target on a header cell's right edge that
 * resizes the containing column (PLO-4818, item 5). Freezes every sibling
 * column to its measured width first so flex-grow columns stop redistributing
 * (only the dragged column changes; the table scrolls). Persistence is handled
 * by the parent via onResizeColumn; the width math lives in reviewLogic
 * (applyColumnResize / MIN_COLUMN_WIDTH).
 *
 * `cellSelector` is the closest-ancestor selector marking a resizable header
 * cell ("[data-col]" for main columns, "[data-subcol]" for entitlement
 * sub-columns), and `attr` the matching data-attribute holding the column id —
 * used to measure and freeze all siblings at drag start.
 */
export const ColumnResizeHandle = ({
  columnId,
  cellSelector,
  attr,
  onResizeColumn,
}: {
  columnId: string;
  cellSelector: string;
  attr: string;
  onResizeColumn: (
    updater: (prev: Record<string, number>) => Record<string, number>,
  ) => void;
}) => (
  <Box
    position="absolute"
    top={0}
    right={0}
    h="full"
    // 6px of GRAB TARGET, but the only thing painted is the 2px rule at its
    // right edge (the ::after) — tinting the whole 6px box read as a slab.
    w="6px"
    zIndex={3}
    cursor="col-resize"
    // Invisible at rest. Hovering ANY part of the header cell reveals the
    // boundary rule (so you can see where to grab); hovering the handle itself
    // strengthens it to the azurite drag colour.
    opacity={0}
    transition="opacity 120ms"
    _groupHover={{ opacity: 1 }}
    _hover={{ opacity: 1 }}
    sx={{
      touchAction: "none",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        width: "2px",
        background: "var(--chakra-colors-border-strong)",
        transition: "background-color 120ms",
      },
      "&:hover::after": {
        background: "var(--chakra-colors-p-300)",
      },
    }}
    onClick={(e) => e.stopPropagation()}
    onPointerDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
      const cellEl = (e.currentTarget as HTMLElement).closest(
        cellSelector,
      ) as HTMLElement | null;
      if (!cellEl) return;
      const startX = e.clientX;
      const startW = cellEl.offsetWidth;
      // Freeze every sibling column to its current measured width, so the
      // flex-grow columns stop redistributing while one column is dragged.
      const frozen: Record<string, number> = {};
      document.querySelectorAll(cellSelector).forEach((el) => {
        const k = (el as HTMLElement).getAttribute(attr);
        if (k)
          frozen[k] = Math.max(
            MIN_COLUMN_WIDTH,
            (el as HTMLElement).offsetWidth,
          );
      });
      // existing overrides win over freshly-measured widths
      onResizeColumn((prev) => ({ ...frozen, ...prev }));
      const move = (ev: PointerEvent) =>
        onResizeColumn((prev) =>
          applyColumnResize(prev, columnId, startW, ev.clientX - startX),
        );
      const up = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
      };
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", up);
    }}
  />
);

const VirtualizedGroup = memo(
  ({
    group,
    selectedAccounts,
    handleSelectAccount,
    handleSelectAction,
    handleEditEntitlements,
    outcomeOptions,
    visibleColumns,
    shouldFillSpace = false,
    splitEntitlementsByType = false,
    onToggleSplitEntitlements,
    canEditEntitlements = false,
    columnWidths = {},
    onResizeColumn,
    onOpenAccount,
    decisionOnly = false,
    reversibleOutcomes,
    entitlementOnly = false,
    floatingScrollbar = true,
    nameSort,
    onToggleNameSort,
    personGrouped = false,
    onGroupAction,
  }: {
    group: { title: string; accounts: MFReviewAccount[] };
    selectedAccounts: string[];
    handleSelectAccount: (id: string, checked: boolean, newS: any) => void;
    handleSelectAction: (id: string, action: string) => void;
    handleEditEntitlements?: (accountId: string) => void;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
    visibleColumns?: string[];
    shouldFillSpace?: boolean;
    splitEntitlementsByType?: boolean;
    onToggleSplitEntitlements?: () => void;
    canEditEntitlements?: boolean;
    // Drag-to-resize column widths (PLO-4818, item 5).
    columnWidths?: Record<string, number>;
    onResizeColumn?: (
      updater: (prev: Record<string, number>) => Record<string, number>,
    ) => void;
    // Open the per-account detail drawer from a row (PLO-4818, item 4).
    onOpenAccount?: (accountId: string) => void;
    // Cycle records decisions only — never deprovisions — so rows never lock
    // (PLO-5083). Forwarded to each AccountRow.
    decisionOnly?: boolean;
    // Locking outcomes this cycle never de-provisions — those rows stay
    // editable (PLO-5749). Forwarded to each AccountRow.
    reversibleOutcomes?: readonly string[];
    // Entitlement-only cycle (PLO-5731): each row is one entitlement, so the
    // column reads "Entitlement" and the rows drop the flag action.
    entitlementOnly?: boolean;
    // Off inside a transformed ancestor (the suggestions carousel's cards),
    // which would anchor the bar's `position: fixed` to itself.
    floatingScrollbar?: boolean;
    // The rows' Account-name order and its header toggle (PLO-6218). The
    // caller sorts; this only draws the arrow and forwards the click.
    nameSort: NameSortDirection;
    onToggleNameSort: () => void;
    // Grouped by person: the header carries the person and their decision
    // buttons, and the rows lead with the resource instead of the person.
    personGrouped?: boolean;
    onGroupAction?: (accountIds: string[], action: string) => void;
  }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Header label for the "previousReview" column, generalised to the actual
    // prior stage ("Stage 1 review", "Stage 2 review", ...). Every row in a
    // group shares the same current-review stage, so the first account's
    // `stage` field is enough — falls back to the default (stage 1) label
    // when the group is empty.
    const previousReviewLabel = useMemo(
      () => previousReviewColumnLabel(group.accounts[0]?.stage),
      [group.accounts],
    );

    // Account first and "actions" last: the table's two pinned edges.
    const orderedColumns = useMemo(
      () => orderPinnedColumns(visibleColumns || []),
      [visibleColumns],
    );

    // Only undecided accounts are selectable — select-all skips any row that
    // already carries a decision (any outcome), so a bulk action can never
    // overwrite an explicit decision.
    const unlockedAccounts = useMemo(() => {
      return group.accounts.filter((account) => !isAccountDecided(account));
    }, [group.accounts]);

    // Membership is asked once per rendered row and once per account on every
    // select-all, so it has to be a Set — as an array scan, selecting every row
    // of a large review cost a pass over the selection per account. Order is
    // still carried by `selectedAccounts` itself. (PLO-5463)
    const selectedAccountIds = useMemo(
      () => new Set(selectedAccounts),
      [selectedAccounts],
    );

    const selectedInGroup = useMemo(() => {
      return unlockedAccounts.filter((account) =>
        selectedAccountIds.has(account.id),
      ).length;
    }, [unlockedAccounts, selectedAccountIds]);

    const handleGroupSelectAll = useCallback(
      (checked: boolean) => {
        // Only include unlocked accounts
        const accountIds = unlockedAccounts.map((account) => account.id);
        if (checked) {
          // Add all unlocked accounts from this group
          const newSelected = [...selectedAccounts];
          const present = new Set(newSelected);
          accountIds.forEach((id) => {
            if (!present.has(id)) {
              present.add(id);
              newSelected.push(id);
            }
          });
          return newSelected;
        } else {
          // Remove all accounts from this group
          const removing = new Set(accountIds);
          return selectedAccounts.filter((id) => !removing.has(id));
        }
      },
      [unlockedAccounts, selectedAccounts],
    );
    const allSelected =
      unlockedAccounts.length > 0 &&
      selectedInGroup === unlockedAccounts.length;
    const someSelected =
      selectedInGroup > 0 && selectedInGroup < unlockedAccounts.length;
    const handleSelectAllChange = useCallback(
      (checked: boolean) => {
        const newSelected = handleGroupSelectAll(checked);
        handleSelectAccount("", false, newSelected);
      },
      [handleGroupSelectAll, handleSelectAccount],
    );

    const parentRef = React.useRef<HTMLDivElement>(null);
    const groupRef = React.useRef<HTMLDivElement>(null);
    const actionButtonCount = rowActionButtonCount(
      outcomeOptions,
      entitlementOnly,
    );
    const { layout: stuckLayout, freezeAccount } = useFrozenPanes(
      shouldFillSpace,
      groupRef,
      parentRef,
      actionsTrackWidth(actionButtonCount),
      [
        isCollapsed,
        group.accounts.length === 0,
        splitEntitlementsByType,
        orderedColumns.join(),
      ].join("|"),
    );

    const rowVirtualizer = useVirtualizer({
      count: group.accounts.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 65, // Min height of each row
      overscan: 5,
      measureElement: (element) =>
        element?.getBoundingClientRect().height || 65,
    });

    // Debug: Log virtualization info

    if (group.accounts.length === 0) return null;

    return (
      <Box
        ref={groupRef}
        width="full"
        borderRadius="component.2xl"
        overflow="hidden"
        borderWidth="1px"
        borderColor="border.default"
        bg="bg.container"
        transition="border-color 120ms, box-shadow 120ms"
        display="flex"
        flexDirection="column"
        flex={shouldFillSpace ? "1" : undefined}
        minH={shouldFillSpace ? 0 : undefined}
        // A page-filling table is capped and stuck below the page's sticky
        // chrome, so the table scrolls (not the page) and its header stays in
        // view (PLO-6216).
        maxH={reviewTableMaxHeight(shouldFillSpace, stuckLayout)}
        position={stuckLayout ? "sticky" : undefined}
        top={stuckLayout ? `${stuckLayout.top}px` : undefined}
      >
        {group.title && personGrouped && (
          <PersonGroupHeader
            person={group.accounts[0]}
            count={group.accounts.length}
            isCollapsed={isCollapsed}
            onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
            actions={
              <GroupActionButtons
                outcomeOptions={outcomeOptions}
                entitlementOnly={entitlementOnly}
                undecidedCount={unlockedAccounts.length}
                onDecide={(action) =>
                  onGroupAction?.(
                    unlockedAccounts.map((account) => account.id),
                    action,
                  )
                }
              />
            }
          />
        )}
        {group.title && !personGrouped && (
          <GroupHeader
            title={group.title}
            count={unlockedAccounts.length}
            selectedCount={selectedInGroup}
            onSelectAll={handleSelectAllChange}
            isCollapsed={isCollapsed}
            onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
          />
        )}

        {/*
          A single scroll container handles BOTH axes (horizontal + vertical)
          and is the virtualizer's scroll element. This is what lets the
          `actions` column pin to the right via `position: sticky; right: 0`:
          sticky is resolved against the nearest scrollport, so header cells and
          row cells must share one scrollport with horizontal scroll. The header
          is `position: sticky; top: 0` so it stays put during vertical scroll,
          and the checkbox and Account cells are `sticky; left` so the
          reviewer can see whose row they are on (PLO-6216).
        */}
        {!isCollapsed && (
          <Box
            ref={parentRef}
            width="full"
            overflow="auto"
            position="relative"
            flex={shouldFillSpace ? "1" : undefined}
            minH={shouldFillSpace ? 0 : undefined}
            maxHeight={shouldFillSpace ? undefined : "700px"}
          >
            {/* Clipped vertically: the pinned columns' edge shadows hang 2000px
                below their header cells, and unclipped they made every group
                scroll on past its last row. `clip` (not `hidden`) leaves the
                scrollport above as the sticky containers' reference. */}
            <Box minWidth="max-content" position="relative" overflowY="clip">
              <Box position="sticky" top={0} zIndex={2}>
                <TableHeader
                  visibleColumns={orderedColumns}
                  splitEntitlementsByType={splitEntitlementsByType}
                  onToggleSplitEntitlements={onToggleSplitEntitlements}
                  columnWidths={columnWidths}
                  onResizeColumn={onResizeColumn}
                  allSelected={allSelected}
                  someSelected={someSelected}
                  onSelectAll={handleSelectAllChange}
                  actionButtonCount={actionButtonCount}
                  freezeAccount={freezeAccount}
                  previousReviewLabel={previousReviewLabel}
                  entitlementOnly={entitlementOnly}
                  nameSort={nameSort}
                  onToggleNameSort={onToggleNameSort}
                  personGrouped={personGrouped}
                />
              </Box>

              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const account = group.accounts[virtualRow.index];
                  return (
                    <div
                      key={virtualRow.index}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      // No fixed height: this is the element `measureElement`
                      // reads, so it must auto-size to its content. Pinning it to
                      // `virtualRow.size` (the estimate until first measure) makes
                      // `getBoundingClientRect().height` always return the
                      // estimate, so taller rows (name + email + title + stage-1
                      // decision line) overflow and overlap the next row.
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <AccountRow
                        key={account.id}
                        account={account}
                        isSelected={selectedAccountIds.has(account.id)}
                        onSelect={handleSelectAccount}
                        onActionSelect={handleSelectAction}
                        onEditEntitlements={handleEditEntitlements}
                        outcomeOptions={outcomeOptions}
                        visibleColumns={orderedColumns}
                        splitEntitlementsByType={splitEntitlementsByType}
                        canEditEntitlements={canEditEntitlements}
                        columnWidths={columnWidths}
                        onOpenAccount={onOpenAccount}
                        decisionOnly={decisionOnly}
                        reversibleOutcomes={reversibleOutcomes}
                        entitlementOnly={entitlementOnly}
                        freezeAccount={freezeAccount}
                        personGrouped={personGrouped}
                      />
                    </div>
                  );
                })}
              </div>
            </Box>
          </Box>
        )}
        {!isCollapsed && floatingScrollbar && (
          <FloatingScrollbar targetRef={parentRef} />
        )}
      </Box>
    );
  },
);

// A person group's header: who the rows belong to, how many there are, and
// the buttons that decide all of their undecided rows. Selecting rows stays in
// the column header's checkbox, so there is no second one here.
const PersonGroupHeader = memo(
  ({
    person,
    count,
    isCollapsed,
    onToggleCollapse,
    actions,
  }: {
    person?: MFReviewAccount;
    count: number;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    actions: React.ReactNode;
  }) => {
    const name =
      person?.member?.name?.trim() ||
      person?.identity?.displayName ||
      person?.identity?.username ||
      person?.identity?.extId ||
      "Unknown person";
    const email = person?.member?.email;

    return (
      <Box
        borderBottom={!isCollapsed ? "1px solid" : "none"}
        borderColor="border.default"
        cursor="pointer"
        onClick={onToggleCollapse}
        _hover={{ bg: "bg.section" }}
      >
        <HStack py={2.5} px={4} justify="space-between" align="center">
          <HStack spacing={3} minW={0}>
            <Icon
              as={isCollapsed ? ChevronRight : ChevronDown}
              boxSize={3.5}
              color="text.body.secondary"
            />
            <Avatar name={name} src={person?.member?.profilePictureUrl || undefined} size="md" />
            <HStack spacing={2} minW={0} align="baseline">
              <Text fontSize="sm" fontWeight="600" color="text.body.primary" noOfLines={1}>
                {name}
              </Text>
              {email && (
                <Text fontSize="sm" color="text.body.secondary" noOfLines={1}>
                  {email}
                </Text>
              )}
            </HStack>
            <Text fontSize="xs" color="text.body.tertiary" fontWeight="450" whiteSpace="nowrap">
              {count} account{count !== 1 ? "s" : ""}
            </Text>
          </HStack>
          {actions}
        </HStack>
      </Box>
    );
  },
);

const GroupHeader = memo(
  ({
    title,
    count,
    selectedCount,
    onSelectAll,
    isCollapsed,
    onToggleCollapse,
  }: {
    title: string;
    count: number;
    selectedCount: number;
    onSelectAll: (checked: boolean) => void;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
  }) => {
    const allSelected = count > 0 && selectedCount === count;
    const someSelected = selectedCount > 0 && selectedCount < count;

    return (
      <Box
        borderBottom={!isCollapsed ? "1px solid" : "none"}
        borderColor="border.default"
        cursor="pointer"
        onClick={onToggleCollapse}
        transition="background-color 120ms"
        position="relative"
        _before={{
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "3px",
          bg: "p.600",
          opacity: 0.5,
          borderTopLeftRadius: "component.md",
        }}
        _hover={{
          bg: "bg.section",
        }}
      >
        <HStack py={2.5} px={4} align="center">
          <HStack spacing={2}>
            <Icon
              as={isCollapsed ? ChevronRight : ChevronDown}
              boxSize={3.5}
              color="text.body.secondary"
            />
            <Box onClick={(e) => e.stopPropagation()}>
              <CustomCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onSelectAll}
              />
            </Box>
            <Text
              fontSize="sm"
              fontWeight="500"
              color="text.body.primary"
              letterSpacing="-0.01em"
            >
              {title}
            </Text>
          </HStack>
          <HStack spacing={3}>
            <Text fontSize="xs" color="text.body.tertiary" fontWeight="450">
              {count} account{count !== 1 ? "s" : ""}
              {selectedCount > 0 && `, ${selectedCount} selected`}
            </Text>
          </HStack>
        </HStack>
      </Box>
    );
  },
);

// Helper to check if a column is an entitlement type column
export const isEntitlementTypeColumn = (column: string): boolean =>
  column.startsWith("entitlement_type_");

// Get entitlement column positions for border styling
const getEntitlementColumnInfo = (visibleColumns: string[]) => {
  const entitlementIndices = visibleColumns
    .map((col, idx) => (isEntitlementTypeColumn(col) ? idx : -1))
    .filter((idx) => idx !== -1);

  if (entitlementIndices.length === 0) return { first: -1, last: -1 };

  return {
    first: entitlementIndices[0],
    last: entitlementIndices[entitlementIndices.length - 1],
  };
};

const ENTITLEMENT_BG = "bg.section";
const ENTITLEMENT_BORDER = "border.default";
const TEXT_PRIMARY = "text.body.primary";
const TEXT_LABEL = "text.body.secondary";

// Pins the "actions" column to the right edge of the horizontal scrollport so
// it stays visible while the rest of the table scrolls underneath it. Shared by
// the header — rows compute their own bg in AccountRow to track
// hover/selected state. The left shadow only reads once content scrolls under
// the column. Exported so AccountRow keeps the offsets in sync.
export const STICKY_ACTIONS_SX = {
  position: "sticky" as const,
  right: 0,
  // Symmetric gutters, applied to both the header and per-row actions cells so
  // they stay aligned: the left one separates the pinned column from the
  // content scrolling underneath, and the right one matches it so the actions
  // aren't jammed against the table's edge.
  pl: 4,
  pr: 4,
  // NO per-cell shadow. Each row's actions cell drawing its own left shadow
  // stacked into a column of separate smudges broken at every row boundary.
  // The pinned edge is now drawn ONCE, by the header cell's ::after (see
  // STICKY_EDGE_SHADOW below), which rides the sticky column and spans the
  // scrollport — so the whole column has one continuous shadow.
};

/* The pinned column's single continuous edge shadow. Lives on the sticky
   HEADER cell and extends the full height of the scrollport beneath it (the
   scroll container clips it), so it tracks the column horizontally and never
   segments per row. */
export const STICKY_EDGE_SHADOW = {
  _after: {
    content: '""',
    position: "absolute" as const,
    top: "100%",
    left: 0,
    right: 0,
    height: "2000px",
    boxShadow: "-6px 0 8px -6px rgba(0, 0, 0, 0.08)",
    pointerEvents: "none" as const,
  },
};

// Freezes a leading cell (checkbox, Account) at the left edge of the
// horizontal scrollport (PLO-6216). The background must be opaque and track the
// row state, or the content scrolling underneath shows through. `left` is a px
// string: a bare number would resolve against the theme's space scale.
export const frozenLeftSx = (left: number, bg: string) => ({
  position: "sticky" as const,
  left: `${left}px`,
  zIndex: 1,
  bg,
});

/* The frozen Account column's right-edge shadow: the mirror of
   STICKY_EDGE_SHADOW, drawn once from the header cell down the scrollport. */
export const FROZEN_EDGE_SHADOW = {
  _after: {
    ...STICKY_EDGE_SHADOW._after,
    boxShadow: "6px 0 8px -6px rgba(0, 0, 0, 0.08)",
  },
};

const HEADER_BG = "bg.container";

// Redesigned table header with grouped entitlement columns
const TableHeader = memo(
  ({
    visibleColumns = [],
    splitEntitlementsByType = false,
    onToggleSplitEntitlements,
    columnWidths = {},
    onResizeColumn,
    allSelected = false,
    someSelected = false,
    onSelectAll,
    actionButtonCount = 4,
    previousReviewLabel,
    entitlementOnly = false,
    freezeAccount = false,
    nameSort,
    onToggleNameSort,
    personGrouped = false,
  }: {
    visibleColumns?: string[];
    splitEntitlementsByType?: boolean;
    onToggleSplitEntitlements?: () => void;
    columnWidths?: Record<string, number>;
    onResizeColumn?: (
      updater: (prev: Record<string, number>) => Record<string, number>,
    ) => void;
    allSelected?: boolean;
    someSelected?: boolean;
    onSelectAll?: (checked: boolean) => void;
    /** action-button count so the pinned actions header aligns with the rows. */
    actionButtonCount?: number;
    /** Stage-specific "previousReview" column header, e.g. "Stage 1 review". */
    previousReviewLabel?: string;
    /** Entitlement-only cycle (PLO-5731) — singularises the entitlement column. */
    entitlementOnly?: boolean;
    /** Freeze the checkbox and Account cells (PLO-6216). */
    freezeAccount?: boolean;
    /** Account-name sort (PLO-6218): the Account header shows it and flips it. */
    nameSort: NameSortDirection;
    onToggleNameSort: () => void;
    /** Grouped by person: the leading column is the row's resource. */
    personGrouped?: boolean;
  }) => {
    const pinLeft = (left: number) =>
      freezeAccount ? frozenLeftSx(left, HEADER_BG) : {};
    const frozenHeaderSx = (column: string) =>
      column === FROZEN_REVIEW_COLUMN && freezeAccount
        ? { ...pinLeft(FROZEN_LEFT_OFFSETS.member), ...FROZEN_EDGE_SHADOW }
        : {};
    // A header cell is resizable unless it's the pinned actions column. The
    // checkbox column has no header cell of its own.
    const isResizable = (column: string) => column !== "actions";
    // Bound once: this header renders the same label in six places across its
    // split and unsplit layouts, and they must all read the same inputs.
    const columnLabel = (column: string) =>
      personGrouped && column === "member"
        ? "Resource"
        : getColumnLabel(column, previousReviewLabel, entitlementOnly);
    // A plain column title, or for the Account column a button that flips
    // the name sort (PLO-6218).
    const headerLabel = (column: string, color: string = TEXT_PRIMARY) => {
      const title = (
        <Text
          color={color}
          fontFamily="body"
          fontSize="md"
          fontWeight={600}
          lineHeight="100%"
        >
          {columnLabel(column)}
        </Text>
      );
      if (column !== "member" || personGrouped) return title;
      return (
        <HStack
          as="button"
          type="button"
          spacing={1}
          onClick={onToggleNameSort}
          aria-label={`${columnLabel(column)}, sorted ${
            nameSort === "asc" ? "A to Z" : "Z to A"
          }. Click to reverse.`}
        >
          {title}
          <Icon
            as={nameSort === "asc" ? ArrowUp : ArrowDown}
            boxSize="12px"
            color="text.body.secondary"
          />
        </HStack>
      );
    };
    const entitlementInfo = getEntitlementColumnInfo(visibleColumns);
    const hasEntitlementColumns = entitlementInfo.first !== -1;
    const entitlementColumnCount = hasEntitlementColumns
      ? entitlementInfo.last - entitlementInfo.first + 1
      : 0;

    // Get entitlement type columns for the sub-header
    const entitlementTypeColumns = visibleColumns.filter(
      isEntitlementTypeColumn,
    );

    // If we have split entitlement columns, render the two-row header
    if (splitEntitlementsByType && hasEntitlementColumns) {
      const lastEntitlementIndex = entitlementTypeColumns.length - 1;

      return (
        <Box
          borderBottom="1px solid"
          borderColor={ENTITLEMENT_BORDER}
          bg="bg.container"
        >
          {/* Row 1: Main headers with Entitlement group header */}
          <Box
            display="grid"
            gridTemplateColumns={getGridTemplateColumns(
              visibleColumns,
              columnWidths,
              actionButtonCount,
            )}
            pl={4}
            alignItems="stretch"
          >
            <Box
              py={3}
              display="flex"
              alignItems="center"
              justifyContent="center"
              {...pinLeft(FROZEN_LEFT_OFFSETS.checkbox)}
            >
              {onSelectAll && (
                <CustomCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onSelectAll}
                />
              )}
            </Box>

            {/* Regular columns before entitlements */}
            {visibleColumns.slice(0, entitlementInfo.first).map((column) => (
              <Box
                key={column}
                data-col={column}
                role="group"
                position="relative"
                py={3}
                display="flex"
                alignItems="center"
                {...frozenHeaderSx(column)}
              >
                {headerLabel(column)}
                {onResizeColumn && isResizable(column) && (
                  <ColumnResizeHandle
                    columnId={column}
                    cellSelector="[data-col]"
                    attr="data-col"
                    onResizeColumn={onResizeColumn}
                  />
                )}
              </Box>
            ))}

            {/* Entitlement group header - spans all entitlement columns */}
            <Box
              gridColumn={`span ${entitlementColumnCount}`}
              bg={ENTITLEMENT_BG}
              borderLeft="1px solid"
              borderRight="1px solid"
              borderTop="1px solid"
              borderColor={ENTITLEMENT_BORDER}
              pl={3}
              mr={3}
              py={2}
              display="flex"
              alignItems="center"
            >
              <HStack spacing={2}>
                <Text
                  color={TEXT_PRIMARY}
                  fontFamily="body"
                  fontSize="md"
                  fontWeight={600}
                  lineHeight="100%"
                >
                  Entitlement
                </Text>
                {onToggleSplitEntitlements && (
                  <Box
                    as="button"
                    onClick={onToggleSplitEntitlements}
                    bg="neutral.75"
                    p="4px"
                    borderRadius="component.md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ bg: "neutral.100" }}
                    boxShadow="xs"
                  >
                    <Icon
                      as={SplitSquareHorizontal}
                      boxSize="12px"
                      color="text.body.secondary"
                    />
                  </Box>
                )}
              </HStack>
            </Box>

            {/* Regular columns after entitlements */}
            {visibleColumns.slice(entitlementInfo.last + 1).map((column) => (
              <Box
                key={column}
                data-col={column}
                role="group"
                py={3}
                display="flex"
                alignItems="center"
                {...(column === "actions"
                  ? {
                      ...STICKY_ACTIONS_SX,
                      ...STICKY_EDGE_SHADOW,
                      zIndex: 1,
                      bg: "bg.container",
                    }
                  : { position: "relative" })}
              >
                {headerLabel(column)}
                {onResizeColumn && isResizable(column) && (
                  <ColumnResizeHandle
                    columnId={column}
                    cellSelector="[data-col]"
                    attr="data-col"
                    onResizeColumn={onResizeColumn}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Row 2: Sub-headers for entitlement types */}
          <Box
            display="grid"
            gridTemplateColumns={getGridTemplateColumns(
              visibleColumns,
              columnWidths,
              actionButtonCount,
            )}
            pl={4}
            alignItems="stretch"
          >
            <Box pb={2} {...pinLeft(FROZEN_LEFT_OFFSETS.checkbox)} />

            {/* Empty cells for columns before entitlements. The Account one
                stays frozen, so sub-headers scroll under it, not past it. */}
            {visibleColumns.slice(0, entitlementInfo.first).map((column) => (
              <Box
                key={`empty-${column}`}
                pb={2}
                {...(column === FROZEN_REVIEW_COLUMN
                  ? pinLeft(FROZEN_LEFT_OFFSETS.member)
                  : {})}
              />
            ))}

            {/* Entitlement sub-headers with background */}
            {entitlementTypeColumns.map((column, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === lastEntitlementIndex;
              const label = columnLabel(column);
              return (
                <Tooltip
                  key={column}
                  label={label}
                  placement="top"
                  hasArrow
                  openDelay={500}
                >
                  <Box
                    data-subcol={column}
                    role="group"
                    position="relative"
                    bg={ENTITLEMENT_BG}
                    borderLeft="1px solid"
                    borderRight={isLast ? "1px solid" : "none"}
                    borderColor={ENTITLEMENT_BORDER}
                    pl={3}
                    mr={isLast ? 3 : 0}
                    pb={2}
                    display="flex"
                    alignItems="center"
                    overflow="hidden"
                  >
                    <Text
                      color={TEXT_LABEL}
                      fontFamily="body"
                      fontSize="md"
                      fontWeight={600}
                      lineHeight="12px"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {label}
                    </Text>
                    {onResizeColumn && (
                      <ColumnResizeHandle
                        columnId={column}
                        cellSelector="[data-subcol]"
                        attr="data-subcol"
                        onResizeColumn={onResizeColumn}
                      />
                    )}
                  </Box>
                </Tooltip>
              );
            })}

            {/* Empty cells for columns after entitlements */}
            {visibleColumns.slice(entitlementInfo.last + 1).map((column) => (
              <Box
                key={`empty-after-${column}`}
                pb={2}
                {...(column === "actions"
                  ? {
                      ...STICKY_ACTIONS_SX,
                      ...STICKY_EDGE_SHADOW,
                      zIndex: 1,
                      bg: "bg.container",
                    }
                  : {})}
              />
            ))}
          </Box>
        </Box>
      );
    }

    // Default single-row header (no split or no entitlement columns)
    // Check if we have an "entitlements" column to show the split button
    const entitlementsColIndex = visibleColumns.findIndex(
      (col) => col === "entitlements",
    );
    const hasEntitlementsCol = entitlementsColIndex !== -1;

    const renderResizeHandle = (column: string) =>
      onResizeColumn && isResizable(column) ? (
        <ColumnResizeHandle
          columnId={column}
          cellSelector="[data-col]"
          attr="data-col"
          onResizeColumn={onResizeColumn}
        />
      ) : null;

    return (
      <Box
        display="grid"
        gridTemplateColumns={getGridTemplateColumns(
          visibleColumns,
          columnWidths,
          actionButtonCount,
        )}
        pl={4}
        py={2.5}
        borderBottom="1px solid"
        borderColor="border.default"
        bg="bg.container"
        alignItems="center"
      >
        <Box
          alignSelf="stretch"
          display="flex"
          alignItems="center"
          justifyContent="center"
          {...pinLeft(FROZEN_LEFT_OFFSETS.checkbox)}
        >
          {onSelectAll && (
            <CustomCheckbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={onSelectAll}
            />
          )}
        </Box>
        {visibleColumns.map((column) => {
          const isEntitlement = isEntitlementTypeColumn(column);

          // Pin the Actions header to the right edge so it stays aligned with
          // the pinned per-row actions cells while the rest scrolls.
          if (column === "actions") {
            return (
              <Box
                key={column}
                {...STICKY_ACTIONS_SX}
                zIndex={1}
                bg="bg.container"
                alignSelf="stretch"
                display="flex"
                alignItems="center"
              >
                <Text
                  color={TEXT_PRIMARY}
                  fontFamily="body"
                  fontSize="md"
                  fontWeight={600}
                  lineHeight="100%"
                >
                  {columnLabel(column)}
                </Text>
              </Box>
            );
          }

          // Show split button next to "Entitlements" column header
          if (column === "entitlements" && onToggleSplitEntitlements) {
            return (
              <HStack
                key={column}
                data-col={column}
                role="group"
                position="relative"
                spacing={2}
                alignSelf="stretch"
              >
                <Text
                  color={TEXT_PRIMARY}
                  fontFamily="body"
                  fontSize="md"
                  fontWeight={600}
                  lineHeight="100%"
                >
                  {columnLabel(column)}
                </Text>
                <Box
                  as="button"
                  onClick={onToggleSplitEntitlements}
                  bg="neutral.75"
                  p="4px"
                  borderRadius="component.md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ bg: "neutral.100" }}
                  boxShadow="xs"
                >
                  <Icon
                    as={SplitSquareHorizontal}
                    boxSize="12px"
                    color="text.body.secondary"
                  />
                </Box>
                {renderResizeHandle(column)}
              </HStack>
            );
          }

          return (
            <Box
              key={column}
              data-col={column}
              role="group"
              position="relative"
              alignSelf="stretch"
              display="flex"
              alignItems="center"
              // Breathing room off the resize rule on the cell's left edge —
              // matched by the row cells so the columns stay aligned.
              pl="md"
              {...frozenHeaderSx(column)}
            >
              {headerLabel(column, isEntitlement ? TEXT_LABEL : TEXT_PRIMARY)}
              {renderResizeHandle(column)}
            </Box>
          );
        })}
      </Box>
    );
  },
);

// The pinned actions track: the actual action-button count (~28px/button incl.
// gap) plus BOTH gutters from STICKY_ACTIONS_SX (pl 16 + pr 16). The track must
// include them, otherwise the pinned cell's content overflows its track (adding
// a second horizontal scrollbar) or the padding just squeezes the icons.
// Floored so a small config never shrinks it.
export const actionsTrackWidth = (actionButtonCount: number): number =>
  Math.max(156, 28 * actionButtonCount + 32);

export const getGridTemplateColumns = (
  visibleColumns: string[] = [],
  // Explicit per-column widths from drag-to-resize (PLO-4818). A column present
  // in this map renders at a fixed px track (so only it changes on resize and
  // the table scrolls); absent columns keep their flex minmax() default.
  columnWidths: Record<string, number> = {},
  // Number of action buttons in the row (outcome icons + the query action), so
  // the pinned actions track is wide enough to show them all. Defaults to 4 for
  // callers that don't know the count; the header + rows must pass the SAME
  // value or the pinned column misaligns.
  actionButtonCount: number = 4,
) => {
  // Start with the checkbox column.
  let template = `${CHECKBOX_TRACK_PX}px`;

  // Add column widths based on their type, using minimum widths to prevent shrinking
  visibleColumns.forEach((col) => {
    // A user-resized column becomes a fixed track. "actions" is pinned/sticky —
    // never resizable — so it always keeps its fixed default below.
    const override = columnWidths[col];
    if (
      col !== "actions" &&
      typeof override === "number" &&
      Number.isFinite(override)
    ) {
      template += ` ${override}px`;
      return;
    }
    switch (col) {
      case "member":
        template += " minmax(240px, 1.5fr)";
        break;
      case "location":
        template += " minmax(150px, 1fr)";
        break;
      case "entitlements":
        template += " minmax(200px, 1fr)";
        break;
      case "lastUsed":
        template += " minmax(150px, 1fr)";
        break;
      case "accessGrantedAt":
        template += " minmax(150px, 1fr)";
        break;
      case "accessExpiresAt":
        template += " minmax(150px, 1fr)";
        break;
      case "identity":
        template += " minmax(200px, 1fr)";
        break;
      case "accessKind":
        template += " minmax(150px, auto)";
        break;
      case "resource":
        template += " minmax(180px, 1fr)";
        break;
      case "role":
        template += " minmax(160px, 1fr)";
        break;
      case "review":
        template += " minmax(130px, auto)";
        break;
      case "previousReview":
        template += " minmax(180px, 1fr)";
        break;
      case "status":
        template += " minmax(120px, 1fr)";
        break;
      case "actions": {
        const actionsWidth = actionsTrackWidth(actionButtonCount);
        template += ` minmax(${actionsWidth}px, ${actionsWidth}px)`;
        break;
      }
      default:
        // Handle entitlement_type_* columns - wider to accommodate long type names
        if (col.startsWith("entitlement_type_")) {
          template += " minmax(220px, 1fr)";
        } else {
          template += " minmax(120px, 1fr)";
        }
    }
  });

  return template;
};

// Column header label mapping
const columnLabels: Record<string, string> = {
  member: "Account",
  review: "Status",
  previousReview: "Previous review",
  location: "Location",
  entitlements: "Entitlements",
  accessGrantedAt: "Access Granted",
  accessExpiresAt: "Access Expires",
  lastUsed: "Last Used",
  identity: "Identity",
  accessKind: "Access",
  resource: "Resource",
  status: "Status",
  actions: "Actions",
};

// Get label for a column, including dynamic entitlement type columns.
// `previousReviewLabel` overrides the static "Previous review" default with
// the stage-specific label ("Stage 1 review", "Stage 2 review", ...) when the
// caller knows the review's stage; omit it to get the generic fallback.
// `entitlementOnly` singularises the entitlements column: in that cycle a row
// carries exactly one entitlement, and a plural header over a single chip reads
// as a truncated list (PLO-5731).
export const getColumnLabel = (
  column: string,
  previousReviewLabel?: string,
  entitlementOnly = false,
): string => {
  if (column.startsWith("entitlement_type_")) {
    return column.replace("entitlement_type_", "");
  }
  if (column === "previousReview" && previousReviewLabel) {
    return previousReviewLabel;
  }
  if (column === "entitlements" && entitlementOnly) {
    return "Entitlement";
  }
  return columnLabels[column] || column;
};

export default VirtualizedGroup;
